// src/components/analyze/BrandNameTab.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Select, Space, Modal, message } from 'antd';
import PromptEditorModal from './ext/PromptEditorModal';
import { useSocketContext } from '../../sockets/SocketContext';
import { useDispatch, useSelector } from 'react-redux';
import { generateBrandName, similarBrandSearchStart } from '../../store/brandNameSlice';
import SuggestionCard from './BrandName/SuggestionCard';
import MktuClassSelector from '../common/MktuClassSelector';

const { Option } = Select;

const BrandNameTab = () => {
  const dispatch = useDispatch();
  const { socket,createNewTaskOnServer } = useSocketContext();

  // Единый ввод для обоих вариантов брендов
  const [brandType, setBrandType] = useState('oneWord'); // 'oneWord' или 'twoWord'
  const [activity, setActivity] = useState('');
  const [audience, setAudience] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  // Состояние для выбора класса МКТУ
  const [mktu, setMktu] = useState([]);

  // Состояния для работы с шаблонами (редактор) и модального окна для ввода пароля
  const [currentTemplates, setCurrentTemplates] = useState(null);
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [password, setPassword] = useState('');
  // Локальное состояние для предложенных брендов (карточки)
  // Каждая карточка: { id, text, locked, type, similarRequested }
  const [suggestions, setSuggestions] = useState([]);
  const processedSuggestionIdsRef = useRef(new Set());
  // Счетчик для уникальных id карточек
  const suggestionIdCounter = useRef(0);
  const generateId = () => {
    suggestionIdCounter.current++;
    return `suggestion-${suggestionIdCounter.current}`;
  };

  // Выбираем данные из Redux для выбранного типа бренда
  const brandState = useSelector((state) =>
    brandType === 'oneWord' ? state.brandName.oneWord : state.brandName.twoWord
  );

  // Обновление карточек при получении данных из Redux
  useEffect(() => {
    if (brandState.brandNames && brandState.brandNames.length > 0) {
      setSuggestions((prev) => {
        // Сохраняем заблокированные карточки
        const locked = prev.filter((s) => s.locked);
        // Формируем новые карточки из полученных имён и добавляем флаг similarRequested: false
        const newUnlocked = brandState.brandNames.slice(0, 10).map((text) => ({
          id: generateId(),
          text,
          locked: false,
          type: brandType,
          similarRequested: false,
        }));
        return [...locked, ...newUnlocked];
      });
    }
  }, [brandState.brandNames, brandType]);


  const similarState = useSelector((state) => state.brandName.similar);

  useEffect(() => {
    // Для каждой карточки, если запрос еще не отправлен (и в сторе нет записи),
    // отправляем запрос и отмечаем её как обработанную.
    suggestions.forEach((suggestion) => {
      if (
        !processedSuggestionIdsRef.current.has(suggestion.id) &&
        !similarState[suggestion.id]
      ) {
        // Отмечаем, что для этой карточки запрос уже отправлен
        processedSuggestionIdsRef.current.add(suggestion.id);
  
        // Обновляем стор, чтобы зафиксировать, что запрос отправлен
        dispatch(similarBrandSearchStart(suggestion.id));
  
        // Отправляем запрос через createNewTaskOnServer
        createNewTaskOnServer(
          {
            similar_brand: suggestion.text,
            quantity: 5,
            suggestionId: suggestion.id,
            mktu: mktu,
          },
          'similar_brand',
          (err, task) => {
            if (err) {
              message.error('Ошибка создания задачи для поиска похожих названий');
            }
          }
        );
      }
    });
  }, [suggestions, similarState, dispatch, createNewTaskOnServer, mktu]);


  // Функция переключения замка у карточки
  const toggleLock = (suggestion) => {
    setSuggestions((prev) =>
      prev.map((item) =>
        item.id === suggestion.id ? { ...item, locked: !item.locked } : item
      )
    );
  };

  // Функция очистки незаблокированных карточек
  const clearSuggestions = () => {
    setSuggestions((prev) => prev.filter((item) => item.locked));
  };

  // Генерация бренда – диспатчим запрос с нужным wordCount
  const generateBrand = () => {
    if (!activity || !audience) {
      message.error(
        `Заполните поля для бренда из ${
          brandType === 'oneWord' ? 'одного слова' : 'двух слов'
        }`
      );
      return;
    }
    // Перед новым запросом оставляем только заблокированные карточки
    setSuggestions((prev) => prev.filter((item) => item.locked));
    dispatch(
      generateBrandName({
        activity,
        audience,
        wordCount: brandType === 'oneWord' ? 1 : 2,
        promptIndex,
        mktu,
        socket,
      })
    );
  };

  // Открытие редактора шаблонов (через ввод пароля)
  const openEditor = () => {
    setIsPasswordModalVisible(true);
  };

  // Обработка подтверждения ввода пароля для получения шаблонов
  const handlePasswordOk = () => {
    if (!password) {
      message.error('Пароль обязателен');
      return;
    }
    socket.emit('get_template', { password }, (response) => {
      if (response.status === 'ok') {
        setCurrentTemplates(response.templates);
        setIsEditorVisible(true);
      } else {
        message.error(response.message || 'Ошибка при получении шаблонов');
      }
      setPassword('');
      setIsPasswordModalVisible(false);
    });
  };

  const handlePasswordCancel = () => {
    setPassword('');
    setIsPasswordModalVisible(false);
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Блок формы с ограниченной шириной */}
      <div style={{ maxWidth: '500px' }}>
        {/* <Button onClick={openEditor}>Редактировать шаблоны</Button> */}

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginTop: 20 
        }}>
          <h3 style={{ margin: 0 }}>Тип нейминга</h3>
          <Button 
            size="small"
            type="text"
            style={{ 
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              marginLeft: 10
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '0';
            }}
            onClick={openEditor}
          >
            ⚙️ Редактировать шаблоны
          </Button>
        </div>
        <Form layout="vertical" style={{ textAlign: 'left' }}>
          <Form.Item label="Язык">
            <Select value={brandType} onChange={(value) => setBrandType(value)}>
              <Option value="oneWord">Русский</Option>
              <Option value="twoWord">Английский</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Род деятельности компании">
            <Input.TextArea
              placeholder="Введите род деятельности"
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          <Form.Item label="Целевая аудитория бренда">
            <Input.TextArea
              placeholder="Введите целевую аудиторию"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          <Form.Item label="Класс МКТУ">
            <MktuClassSelector
              value={mktu}
              onChange={setMktu}
              placeholder="МКТУ"
            />
          </Form.Item>
          <Form.Item label="Вариант нейминга">
            <Select value={promptIndex} onChange={(value) => setPromptIndex(value)}>
              <Option value={0}>Одно слово</Option>
              <Option value={1}>Словосочетание</Option>
              <Option value={2}>Фантазийное</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={generateBrand} loading={brandState.loading}>
                Создать обозначение
              </Button>
              <Button onClick={clearSuggestions}>Очистить</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* Блок результатов, занимающий всю ширину */}
      <div style={{ marginTop: 30 }}>
        <h3>Предложения брендов</h3>
        {suggestions.length > 0 ? (
          suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              onToggleLock={toggleLock}
              socket={socket}
            />
          ))
        ) : (
          <p>Нет результатов</p>
        )}
      </div>

      <PromptEditorModal
        open={isEditorVisible}
        currentTemplates={currentTemplates}
        onClose={() => setIsEditorVisible(false)}
        onSave={(newTemplates) => setCurrentTemplates(newTemplates)}
        socket={socket}
        initialType={brandType}
        initialIndex={promptIndex}
      />

      <Modal
        title="Введите пароль для получения шаблонов"
        open={isPasswordModalVisible}
        onOk={handlePasswordOk}
        onCancel={handlePasswordCancel}
        okText="Подтвердить"
        cancelText="Отмена"
        width="min(960px, 92vw)"
        bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', width: '100%' }}
        centered
      >
        <Input.Password
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Введите пароль"
        />
      </Modal>
    </div>
  );
};

export default BrandNameTab;
